import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CartContext } from '../App';
import { toAbsoluteImageUrl } from '../utils/image';

const Cart = () => {
  const { cart, updateQuantity, removeFromCart } = useContext(CartContext);
  const navigate = useNavigate();

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    updateQuantity(productId, newQuantity);
  };

  const handleRemove = (productId, productName) => {
    removeFromCart(productId);
    toast.success(`Removed ${productName} from cart`);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  return (
    <div data-testid="cart-page">
      <Navbar />
      
      <div className="cart-container">
        <h1 className="cart-title" data-testid="cart-title">Shopping Cart</h1>

        {cart.length === 0 ? (
          <div className="empty-cart" data-testid="empty-cart">
            <ShoppingBag size={80} strokeWidth={1} />
            <h2>Your cart is empty</h2>
            <p>Add some beautiful products to your cart!</p>
            <Link to="/shop" className="shop-button" data-testid="continue-shopping">
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className="cart-layout">
            {/* Cart Items */}
            <div className="cart-items" data-testid="cart-items">
              {cart.map((item) => (
                <div key={item.id} className="cart-item" data-testid={`cart-item-${item.id}`}>
                  <img
                    src={toAbsoluteImageUrl(item.images[0]) || 'https://via.placeholder.com/120x120?text=No+Image'}
                    alt={item.name}
                    className="cart-item-image"
                    data-testid={`cart-item-image-${item.id}`}
                  />
                  
                  <div className="cart-item-details">
                    <h3 className="cart-item-name" data-testid={`cart-item-name-${item.id}`}>
                      {item.name}
                    </h3>
                    <p className="cart-item-price" data-testid={`cart-item-price-${item.id}`}>
                      ${item.price.toFixed(2)}
                    </p>
                  </div>

                  <div className="cart-item-actions">
                    <div className="quantity-controls">
                      <button 
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        className="qty-btn"
                        data-testid={`decrease-qty-${item.id}`}
                        disabled={item.quantity <= 1}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="quantity-display" data-testid={`quantity-${item.id}`}>
                        {item.quantity}
                      </span>
                      <button 
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        className="qty-btn"
                        data-testid={`increase-qty-${item.id}`}
                      >
                        <Plus size={16} />
                      </button>
                    </div>

                    <p className="item-total" data-testid={`item-total-${item.id}`}>
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>

                    <button 
                      onClick={() => handleRemove(item.id, item.name)}
                      className="remove-btn"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Cart Summary */}
            <div className="cart-summary" data-testid="cart-summary">
              <h2 className="summary-title">Order Summary</h2>
              
              <div className="summary-row">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              
              <div className="summary-row">
                <span>Shipping</span>
                <span className="shipping-note">Calculated at checkout</span>
              </div>

              <div className="summary-total">
                <span>Total</span>
                <span data-testid="cart-total">${subtotal.toFixed(2)}</span>
              </div>

              <button 
                onClick={handleCheckout}
                className="checkout-btn"
                data-testid="proceed-to-checkout"
              >
                Proceed to Checkout
              </button>

              <Link to="/shop" className="continue-shopping-link" data-testid="continue-shopping-link">
                Continue Shopping
              </Link>
            </div>
          </div>
        )}
      </div>

      <Footer />

      <style jsx>{`
        .cart-container {
          margin-top: 80px;
          padding: 3rem 2rem;
          max-width: 1400px;
          margin-left: auto;
          margin-right: auto;
          min-height: calc(100vh - 200px);
        }

        .cart-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 2rem;
        }

        .empty-cart {
          text-align: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .empty-cart svg {
          color: #CBD5E0;
          margin-bottom: 1.5rem;
        }

        .empty-cart h2 {
          font-size: 1.8rem;
          font-weight: 600;
          color: #2D3748;
          margin-bottom: 0.5rem;
        }

        .empty-cart p {
          color: #718096;
          margin-bottom: 2rem;
        }

        .shop-button {
          display: inline-block;
          background: linear-gradient(135deg, #FF9B9B 0%, #B4A7FF 100%);
          color: white;
          padding: 1rem 2rem;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 600;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 20px rgba(255, 155, 155, 0.3);
        }

        .shop-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(255, 155, 155, 0.4);
        }

        .cart-layout {
          display: grid;
          grid-template-columns: 1fr 400px;
          gap: 2rem;
        }

        .cart-items {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .cart-item {
          display: flex;
          gap: 1.5rem;
          background: white;
          padding: 1.5rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .cart-item-image {
          width: 120px;
          height: 120px;
          object-fit: cover;
          border-radius: 10px;
          background: #F7FAFC;
        }

        .cart-item-details {
          flex: 1;
        }

        .cart-item-name {
          font-size: 1.2rem;
          font-weight: 600;
          color: #2D3748;
          margin-bottom: 0.5rem;
        }

        .cart-item-price {
          font-size: 1.1rem;
          color: #FF9B9B;
          font-weight: 600;
        }

        .cart-item-actions {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .quantity-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #F7FAFC;
          padding: 0.5rem;
          border-radius: 10px;
        }

        .qty-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          background: white;
          color: #4A5568;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .qty-btn:hover:not(:disabled) {
          background: #FF9B9B;
          color: white;
        }

        .qty-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .quantity-display {
          min-width: 30px;
          text-align: center;
          font-weight: 600;
        }

        .item-total {
          font-size: 1.2rem;
          font-weight: 700;
          color: #2D3748;
          min-width: 80px;
          text-align: right;
        }

        .remove-btn {
          background: #FED7D7;
          color: #E53E3E;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .remove-btn:hover {
          background: #E53E3E;
          color: white;
        }

        .cart-summary {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          height: fit-content;
          position: sticky;
          top: 100px;
        }

        .summary-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #F7FAFC;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1rem;
          color: #4A5568;
        }

        .shipping-note {
          font-size: 0.9rem;
          color: #718096;
          font-style: italic;
        }

        .summary-total {
          display: flex;
          justify-content: space-between;
          font-size: 1.3rem;
          font-weight: 700;
          color: #2D3748;
          padding-top: 1rem;
          margin-top: 1rem;
          border-top: 2px solid #F7FAFC;
          margin-bottom: 2rem;
        }

        .checkout-btn {
          width: 100%;
          background: linear-gradient(135deg, #FF9B9B 0%, #B4A7FF 100%);
          color: white;
          padding: 1.2rem;
          border-radius: 50px;
          border: none;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 20px rgba(255, 155, 155, 0.3);
          margin-bottom: 1rem;
        }

        .checkout-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(255, 155, 155, 0.4);
        }

        .continue-shopping-link {
          display: block;
          text-align: center;
          color: #4A5568;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.3s ease;
        }

        .continue-shopping-link:hover {
          color: #FF9B9B;
        }

        @media (max-width: 968px) {
          .cart-container {
            margin-top: 60px;
            padding: 2rem 1rem;
          }

          .cart-layout {
            grid-template-columns: 1fr;
          }

          .cart-summary {
            position: static;
          }

          .cart-item {
            flex-direction: column;
          }

          .cart-item-actions {
            justify-content: space-between;
          }
        }
      `}</style>
    </div>
  );
};

export default Cart;